import type { Map } from 'mapbox-gl';

/** Coordal land tile sources + style layers that must live at root for toggles. */
export const COORDAL_LAND_SOURCE_IDS = ['ghana-landuse', 'ghana-landcover'] as const;

export const COORDAL_LAND_LAYER_IDS = [
  'landcover',
  'landuse',
  'recreation-polygon',
  'national-park',
] as const;

export const COORDAL_NATURAL_LABEL_SOURCE_ID = 'ghana-natural-labels' as const;

export const COORDAL_NATURAL_LABEL_LAYER_IDS = [
  'waterway-label',
  'natural-line-label',
  'natural-point-label',
  'water-line-label',
  'water-point-label',
  'ghana-natural-labels',
] as const;

/** Ghana tile sources + layers that must be reachable via getLayer/setLayoutProperty. */
export const COORDAL_TILE_PROMOTIONS: ReadonlyArray<{
  sources: readonly string[];
  layers: readonly string[];
}> = [
    { sources: COORDAL_LAND_SOURCE_IDS, layers: COORDAL_LAND_LAYER_IDS },
    { sources: [COORDAL_NATURAL_LABEL_SOURCE_ID], layers: COORDAL_NATURAL_LABEL_LAYER_IDS },
    { sources: ['ghana-place-labels'], layers: ['ghana-city-labels', 'ghana-town-labels', 'ghana-village-labels', 'ghana-region-labels', 'ghana-district-labels'] },
    { sources: ['ghana-pois', 'ghana-landmarks'], layers: ['ghana-poi-labels', 'ghana-landmark-labels', 'golf-hole-label'] },
    { sources: ['ghana-road-labels'], layers: ['ghana-road-labels'] },
    { sources: ['ghana-traffic-signals'], layers: ['traffic-signals-symbol', 'traffic-signals-label'] },
    {
      sources: ['ghana-transport'],
      layers: [
        'transit-trotro-stop',
        'transit-lorry-park',
        'transit-taxi-rank',
        'transit-bus-stop',
        'transit-rail-station',
      ],
    },
    { sources: ['ghana-aeroway'], layers: ['aeroway-polygon', 'aeroway-line', 'airport-label'] },
    { sources: ['ghana-motorway-junctions'], layers: ['road-exit-shield'] },
    { sources: ['ghana-building-numbers'], layers: ['building-number-labels'] },
    { sources: ['ghana-trees'], layers: ['ghana-trees-tile', 'ghana-trees-model'] },
    { sources: ['ghana-world-countries'], layers: ['world-countries-outline', 'world-countries-label'] },
    { sources: ['ghana-admin-boundaries'], layers: ['ghana-country-boundary', 'ghana-regions-boundary', 'ghana-districts-boundary'] },
    { sources: ['ghana-water'], layers: ['ghana-water'] },
    { sources: ['ghana-waterways'], layers: ['waterway', 'waterway-shadow', 'ghana-waterways'] },
    {
      sources: ['ghana-structures'],
      layers: [
        'land-structure-polygon',
        'land-structure-line',
        'gate-fence-hedge-shade',
        'fence-line',
        'gate-fence-hedge',
        'crosswalks',
        'building-entrance',
        'structure-furniture-symbol',
        'structures-label',
      ],
    },
    { sources: ['ghana-service-areas'], layers: ['ghana-service-areas-fill', 'ghana-service-areas-line', 'postcode-service-area-fill', 'postcode-service-area-outline', 'postcode-service-area-label'] },
    { sources: ['ghana-hillshade'], layers: ['ghana-hillshade-shadow', 'ghana-hillshade-highlight'] },
    { sources: ['ghana-contours'], layers: ['ghana-contours-minor', 'ghana-contours-minor-label', 'ghana-contours-major', 'ghana-contours-label'] },
    { sources: ['ghana-buildings'], layers: ['2d-building', '2d-building-outline-ghana', '3d-building', 'building-underground'] },
  ];

/** Mapbox throws if getStyle() runs before the style finishes loading. */
export function isMapStyleReady(map: Map): boolean {
  try {
    return map.isStyleLoaded();
  } catch {
    return false;
  }
}

/**
 * Run overlay/layer work once the map can accept addSource/addLayer.
 * Always attempts apply immediately; Mapbox Standard may report loaded/styleLoaded false
 * while the map is already interactive, and load/style.load may not fire again.
 */
export function runWhenMapStyleReady(map: Map, apply: () => void): void {
  let attempts = 0;
  const maxAttempts = 25;

  const attempt = () => {
    if (attempts >= maxAttempts) return;
    attempts += 1;

    try {
      apply();
    } catch {
      map.once('idle', attempt);
      map.once('style.load', attempt);
      map.once('load', attempt);
    }
  };

  attempt();
}

function getStyleSafe(map: Map): any | null {
  if (!isMapStyleReady(map)) return null;
  try {
    return (map as any).getStyle();
  } catch {
    return null;
  }
}

/**
 * Recursively resolve ALL ["config", "propName"] expressions in a value.
 * Walks the entire expression tree and replaces config references with
 * their default values from the import's schema. This is required because
 * config expressions only work inside Mapbox import scope — once promoted
 * to root level, they evaluate to null and break paint/layout properties.
 */
function resolveConfigExpressions(value: any, defaults: Record<string, any>): any {
  if (!Array.isArray(value)) return value;

  if (value[0] === 'config' && value.length === 2) {
    const key = value[1];
    return key in defaults ? defaults[key] : value;
  }

  return value.map((item: any) => resolveConfigExpressions(item, defaults));
}

function resolveLayerConfigExpressions(layer: any, defaults: Record<string, any>): any {
  const resolved: any = { ...layer };

  if (resolved.layout) {
    const layout: any = { ...resolved.layout };
    for (const key of Object.keys(layout)) {
      layout[key] = resolveConfigExpressions(layout[key], defaults);
    }
    resolved.layout = layout;
  }

  if (resolved.paint) {
    const paint: any = { ...resolved.paint };
    for (const key of Object.keys(paint)) {
      paint[key] = resolveConfigExpressions(paint[key], defaults);
    }
    resolved.paint = paint;
  }

  if (resolved.filter) {
    resolved.filter = resolveConfigExpressions(resolved.filter, defaults);
  }

  return resolved;
}

function extractSchemaDefaults(schema: Record<string, any>): Record<string, any> {
  const defaults: Record<string, any> = {};
  for (const [key, entry] of Object.entries(schema)) {
    if (entry && typeof entry === 'object' && 'default' in entry) {
      defaults[key] = entry.default;
    }
  }
  return defaults;
}

function collectStyleCatalog(style: any): { allSources: Record<string, any>; allLayers: any[]; defaults: Record<string, any> } {
  const importData = style?.imports?.[0]?.data;
  const importSchema = importData?.schema || {};
  const rootSchema = style?.schema || {};
  const mergedSchema = { ...importSchema, ...rootSchema };
  return {
    allSources: { ...(style.sources || {}), ...(importData?.sources || {}) },
    allLayers: [...(style.layers || []), ...(importData?.layers || [])],
    defaults: extractSchemaDefaults(mergedSchema),
  };
}

/**
 * Promote all sources and layers from Mapbox's internal import back to root level.
 */
export function promoteLayersToRoot(map: Map) {
  const style = getStyleSafe(map);
  if (!style) return;

  const importData = style?.imports?.[0]?.data;
  if (!importData) return;

  const importedSources = importData.sources || {};
  const importedLayers: any[] = importData.layers || [];
  const importSchema = importData.schema || {};

  if (importedLayers.length === 0) return;

  const defaults = extractSchemaDefaults(importSchema);

  let sourcesAdded = 0;
  for (const [sourceId, sourceDef] of Object.entries(importedSources)) {
    if (map.getSource(sourceId)) continue;
    try {
      const def: any = sourceId === 'ghana-buildings'
        ? { ...(sourceDef as any), promoteId: 'id' }
        : sourceDef;
      map.addSource(sourceId, def);
      sourcesAdded++;
    } catch {
      // Source may already exist
    }
  }

  let layersAdded = 0;
  let layersFailed = 0;
  for (const layer of importedLayers) {
    if (!layer.id || layer.type === 'slot') continue;
    if (map.getLayer(layer.id)) continue;

    try {
      const resolved = resolveLayerConfigExpressions(layer, defaults);
      map.addLayer(resolved);
      layersAdded++;
    } catch {
      layersFailed++;
    }
  }

  if (import.meta.env.DEV && (layersAdded > 0 || sourcesAdded > 0)) {
    console.debug(
      `[map] promoted ${sourcesAdded} sources, ${layersAdded} layers to root (${layersFailed} failed)`,
    );
  }
}

function ensureTilePromotionAtRoot(
  map: Map,
  promotion: { sources: readonly string[]; layers: readonly string[] },
) {
  const style = getStyleSafe(map);
  if (!style) return;

  const { allSources, allLayers, defaults } = collectStyleCatalog(style);

  for (const sourceId of promotion.sources) {
    if (map.getSource(sourceId)) continue;
    const sourceDef = allSources[sourceId];
    if (!sourceDef) continue;
    try {
      const def: any = sourceId === 'ghana-buildings'
        ? { ...sourceDef, promoteId: 'id' }
        : sourceDef;
      map.addSource(sourceId, def);
    } catch {
      // already registered
    }
  }

  for (const layerId of promotion.layers) {
    if (map.getLayer(layerId)) continue;
    const layerDef = allLayers.find((layer) => layer?.id === layerId);
    if (!layerDef || layerDef.type === 'slot') continue;
    try {
      const resolved = Object.keys(defaults).length
        ? resolveLayerConfigExpressions(layerDef, defaults)
        : { ...layerDef };
      map.addLayer(resolved);
    } catch {
      // duplicate or ordering conflict
    }
  }
}

/** Ensure every Coordal Ghana tile source + toggle layer is registered at style root. */
export function ensureAllCoordalTileLayersAtRoot(map: Map) {
  for (const promotion of COORDAL_TILE_PROMOTIONS) {
    ensureTilePromotionAtRoot(map, promotion);
  }
}

/** Layer ids bound to one or more tile sources (for source-wide toggles). */
export function getLayerIdsForSources(map: Map, sourceIds: readonly string[]): string[] {
  const style = getStyleSafe(map);
  if (!style?.layers) return [];

  const ids: string[] = [];
  for (const layer of style.layers as Array<{ id?: string; source?: string }>) {
    if (layer?.id && layer.source && sourceIds.includes(layer.source)) {
      ids.push(layer.id);
    }
  }
  return ids;
}

/** Run after every style load before layer toggles are applied. */
export function prepareMapForLayerToggles(map: Map) {
  if (!isMapStyleReady(map)) return;
  promoteLayersToRoot(map);
  ensureAllCoordalTileLayersAtRoot(map);
}

/** @deprecated Use ensureAllCoordalTileLayersAtRoot */
export function ensureGhanaLandTileLayersAtRoot(map: Map) {
  ensureTilePromotionAtRoot(map, { sources: COORDAL_LAND_SOURCE_IDS, layers: COORDAL_LAND_LAYER_IDS });
}

/** @deprecated Use ensureAllCoordalTileLayersAtRoot */
export function ensureGhanaNaturalLabelLayersAtRoot(map: Map) {
  ensureTilePromotionAtRoot(map, {
    sources: [COORDAL_NATURAL_LABEL_SOURCE_ID],
    layers: COORDAL_NATURAL_LABEL_LAYER_IDS,
  });
}
